import { useState, useEffect, useRef } from 'react';
import { useAuth } from "../auth-context";
import QRCode from 'qrcode';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../ui/table";
import { Badge } from "../ui/badge";
import {
    Search,
    UserPlus,
    ExternalLink,
    Mail,
    CreditCard,
    Loader2,
    CheckCircle2,
    Clock,
    AlertCircle,
    Calendar,
    Shield,
    QrCode as QrIcon,
    ArrowUpCircle,
    RefreshCw
} from "lucide-react";
import api from "../../src/api";
import { toast } from "sonner";
import { isPaidStatus, getRegStatusInfo } from "@/lib/registration-utils";
import {
    Dialog,
    DialogContent,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { PERMISSIONS } from "../../../shared/permissions";

interface Registration {
    id: string;
    folio: string;
    nombre: string;
    apellidoPaterno: string;
    apellidoMaterno: string;
    correo: string;
    status: string;
    role: string;
    tipoParticipante: string;
    participationMode?: string | null;
    isVirtualAttendance: boolean;
    virtualAccessSentAt?: string | null;
    virtualAccessScheduledSendAt?: string | null;
    paidAt?: string | null;
    institucion: string;
    createdAt: string;
    eventId?: string; // Added for Global Action Logic
    event?: { name: string; axis?: string | null };
}

interface HelpDeskViewProps {
    selectedEventId: string;
}

interface RegistrationsListResponse {
    data: Registration[];
    meta: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
    };
}

interface ConfirmPaymentResponse {
    success: boolean;
    status: string;
    emailSent: boolean;
    paidAt: string;
    warning?: string;
}

interface VirtualAccessDeliveryResponse {
    message: string;
    status: string;
    virtualAccessSentAt: string;
}

interface CredentialViewData {
    mode: 'QR' | 'VIRTUAL';
    token?: string;
    folio: string;
    nombre: string;
    eventName: string;
    date?: string;
    time?: string;
    timezone?: string;
    zoomUrl?: string;
    passcode?: string | null;
}

type SyswebStatusState = 'NOT_QUERIED' | 'CONFIRMED' | 'NOT_CONFIRMED' | 'UNAVAILABLE';

interface SyswebStatusResponse {
    state: Exclude<SyswebStatusState, 'NOT_QUERIED'>;
    checkedAt: string;
    rawStatus?: string;
    paidAt?: string | null;
}

interface SyswebConciliationResponse {
    state: Exclude<SyswebStatusState, 'NOT_QUERIED'>;
    outcome: 'PAID_APPLIED' | 'ALREADY_PROCESSED';
    message: string;
    registration?: Pick<Registration, 'status' | 'paidAt' | 'virtualAccessSentAt'> & {
        credentialSentAt?: string | null;
    };
}

const SPEAKER_ALLOWED_PAYMENT_STATUSES = new Set([
    'APPROVED_TO_PAY',
    'PAID',
    'MATERIAL_UPLOADED',
    'CREDENTIAL_ISSUED',
]);

const SPEAKER_PAYMENT_BLOCKED_HELP_TEXT = 'No disponible: el ponente aún no está autorizado para pago.';

function isSpeakerPaymentAllowed(registration?: Pick<Registration, 'role' | 'status'> | null): boolean {
    return !registration || registration.role !== 'SPEAKER' || SPEAKER_ALLOWED_PAYMENT_STATUSES.has(registration.status);
}

export function HelpDeskView({ selectedEventId }: HelpDeskViewProps) {
    const { user: currentUser } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState<Registration[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchScope, setSearchScope] = useState<'event' | 'global'>('event');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [roleFilter, setRoleFilter] = useState<'ALL' | 'ATTENDEE' | 'SPEAKER'>('ALL');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false
    });
    const [searchValidationError, setSearchValidationError] = useState<string | null>(null);

    const canGlobalSearch = currentUser?.isSuperAdmin ||
        currentUser?.permissions?.includes('can_global_helpdesk');
    const canUpgradeParticipant = currentUser?.isSuperAdmin ||
        currentUser?.permissions?.includes('can_upgrade_participant');

    // Modal states
    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [isVipModalOpen, setIsVipModalOpen] = useState(false);
    const [selectedReg, setSelectedReg] = useState<Registration | null>(null);
    const canManualPayment = Boolean(
        currentUser?.isSuperAdmin ||
        currentUser?.permissions?.includes('*') ||
        currentUser?.globalPermissions?.includes(PERMISSIONS.MANUAL_PAYMENT) ||
        (selectedReg?.eventId && currentUser?.permissionsByEvent?.[selectedReg.eventId]?.includes(PERMISSIONS.MANUAL_PAYMENT))
    );
    const canResendPhysicalTicket = Boolean(
        currentUser?.isSuperAdmin ||
        currentUser?.permissions?.includes('*') ||
        currentUser?.globalPermissions?.includes(PERMISSIONS.RESEND_TICKET) ||
        (selectedReg?.eventId && currentUser?.permissionsByEvent?.[selectedReg.eventId]?.includes(PERMISSIONS.RESEND_TICKET))
    );
    const canSyncSyswebPayment = Boolean(
        currentUser?.isSuperAdmin ||
        currentUser?.permissions?.includes('*') ||
        currentUser?.globalPermissions?.includes(PERMISSIONS.SYNC_SYSWEB_PAYMENT) ||
        (selectedReg?.eventId && currentUser?.permissionsByEvent?.[selectedReg.eventId]?.includes(PERMISSIONS.SYNC_SYSWEB_PAYMENT))
    );

    // Action states
    const [isProcessing, setIsProcessing] = useState(false);
    const [justification, setJustification] = useState('');
    const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
    const [syswebStatus, setSyswebStatus] = useState<SyswebStatusState>('NOT_QUERIED');
    const [syswebCheckedAt, setSyswebCheckedAt] = useState<string | null>(null);
    const [isCheckingSysweb, setIsCheckingSysweb] = useState(false);

    // QR Visualization state
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [qrData, setQrData] = useState<CredentialViewData | null>(null);
    const qrCanvasRef = useRef<HTMLCanvasElement>(null);

    // VIP Form State
    const [vipForm, setVipForm] = useState({
        nombre: '',
        apellidoPaterno: '',
        apellidoMaterno: '',
        correo: '',
        edad: 20,
        genero: 'mujer',
        tipoParticipante: 'estudiante-externo',
        institucion: '',
        justification: ''
    });

    useEffect(() => {
        setPage(1);
    }, [selectedEventId, searchScope, statusFilter, roleFilter, searchQuery]);

    // List with debounce effect
    useEffect(() => {
        if (searchScope === 'event' && !selectedEventId) {
            setResults([]);
            setMeta((prev) => ({ ...prev, total: 0, totalPages: 1, hasNext: false, hasPrev: false }));
            return;
        }

        const trimmedQuery = searchQuery.trim();
        const isBraceletSearch = /^[1-9]\d{0,6}$/.test(trimmedQuery);
        if (trimmedQuery.length > 0 && trimmedQuery.length < 3 && !isBraceletSearch) {
            setSearchValidationError('La busqueda requiere minimo 3 caracteres.');
            setResults([]);
            setMeta((prev) => ({ ...prev, total: 0, totalPages: 1, hasNext: false, hasPrev: false }));
            return;
        }

        setSearchValidationError(null);
        const timeout = setTimeout(async () => {
            setIsLoading(true);
            try {
                const params: Record<string, string | number> = {
                    page,
                    limit: 10,
                };

                if (searchScope === 'event') {
                    params.eventId = selectedEventId;
                } else {
                    params.global = 'true';
                }
                if (trimmedQuery.length >= 3 || isBraceletSearch) {
                    params.q = trimmedQuery;
                }
                if (statusFilter !== 'ALL') {
                    params.status = statusFilter;
                }
                if (roleFilter !== 'ALL') {
                    params.role = roleFilter;
                }

                const { data } = await api.get<RegistrationsListResponse>('/registrations', { params });
                setResults(Array.isArray(data) ? data : data.data);
                setMeta(Array.isArray(data)
                    ? { page: 1, limit: 10, total: data.length, totalPages: 1, hasNext: false, hasPrev: false }
                    : data.meta
                );
            } catch (error) {
                console.error("Search error", error);
                setResults([]);
                setMeta((prev) => ({ ...prev, total: 0, totalPages: 1, hasNext: false, hasPrev: false }));
            } finally {
                setIsLoading(false);
            }
        }, 300);

        return () => clearTimeout(timeout);
    }, [searchQuery, selectedEventId, searchScope, statusFilter, roleFilter, page]);

    const handleCheckSyswebStatus = async () => {
        if (!selectedReg) return;

        setIsCheckingSysweb(true);
        try {
            const { data } = await api.get<SyswebStatusResponse>(`/helpdesk/${selectedReg.id}/sysweb-status`);
            setSyswebStatus(data.state);
            setSyswebCheckedAt(data.checkedAt);
        } catch (error: any) {
            setSyswebStatus('UNAVAILABLE');
            setSyswebCheckedAt(error?.response?.data?.checkedAt || new Date().toISOString());
            toast.error(error?.response?.data?.message || 'No fue posible consultar SYSWEB.');
        } finally {
            setIsCheckingSysweb(false);
        }
    };

    const handleApplySyswebPayment = async () => {
        if (!selectedReg || !canSyncSyswebPayment || syswebStatus !== 'CONFIRMED') return;

        setIsProcessing(true);
        try {
            const { data } = await api.post<SyswebConciliationResponse>(
                `/helpdesk/${selectedReg.id}/sysweb-conciliate`,
                {},
            );
            setSyswebStatus(data.state);

            const refreshedRegistration = data.registration;
            if (refreshedRegistration) {
                const refreshed = {
                    ...selectedReg,
                    status: refreshedRegistration.status,
                    paidAt: refreshedRegistration.paidAt,
                    virtualAccessSentAt: refreshedRegistration.virtualAccessSentAt,
                };
                setSelectedReg(refreshed);
                setResults((previous) => previous.map((registration) => (
                    registration.id === selectedReg.id ? { ...registration, ...refreshed } : registration
                )));
            }

            toast.success(data.outcome === 'ALREADY_PROCESSED'
                ? 'El pago ya habia sido aplicado; el registro fue actualizado.'
                : 'Pago SYSWEB aplicado correctamente.');
        } catch (error: any) {
            const responseState = error?.response?.data?.state as SyswebStatusState | undefined;
            if (responseState === 'NOT_CONFIRMED' || responseState === 'UNAVAILABLE') {
                setSyswebStatus(responseState);
                setSyswebCheckedAt(new Date().toISOString());
            }
            toast.error(error?.response?.data?.message || 'No fue posible aplicar el pago confirmado por SYSWEB.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleResendQR = async (id: string) => {
        if (
            selectedReg &&
            !selectedReg.isVirtualAttendance &&
            (!canResendPhysicalTicket || !isPaidStatus(selectedReg.status))
        ) {
            return;
        }
        if (selectedReg?.isVirtualAttendance && !selectedReg.paidAt) {
            toast.error('No se puede reenviar: el registro virtual no tiene pago confirmado.');
            return;
        }
        if (
            selectedReg?.isVirtualAttendance &&
            !selectedReg.virtualAccessSentAt &&
            (!selectedReg.virtualAccessScheduledSendAt || new Date(selectedReg.virtualAccessScheduledSendAt).getTime() > Date.now())
        ) {
            toast.error('El acceso Zoom todavía no puede enviarse: la fecha programada no ha sido alcanzada.');
            return;
        }
        if (!isSpeakerPaymentAllowed(selectedReg)) {
            toast.error(SPEAKER_PAYMENT_BLOCKED_HELP_TEXT);
            return;
        }
        setIsProcessing(true);
        try {
            const isGlobalMode = searchScope === 'global';
            console.log(`[HelpDesk] ${isGlobalMode ? 'GLOBAL' : 'LOCAL'} Resend Requested for ID: ${id}`);

            const { data } = await api.post<VirtualAccessDeliveryResponse>(`/helpdesk/${id}/resend-ticket`, {}, {
                params: isGlobalMode ? { global: 'true' } : {},
                headers: {
                    'x-selected-event-id': selectedReg?.isVirtualAttendance
                        ? selectedEventId
                        : selectedReg?.eventId || selectedEventId
                }
            });

            toast.success(selectedReg?.isVirtualAttendance
                ? data.message
                : 'Boleto reenviado correctamente.');
            if (selectedReg?.isVirtualAttendance) {
                setResults((previous) => previous.map((registration) => registration.id === id
                    ? { ...registration, status: data.status, virtualAccessSentAt: data.virtualAccessSentAt }
                    : registration));
            }
            setIsActionModalOpen(false);
        } catch (error: any) {
            console.error('[HelpDesk] Error Resend:', error);
            toast.error(error.response?.data?.message || "Error al reenviar boleto.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleConfirmPayment = async () => {
        if (!selectedReg || !justification || !canManualPayment) return;
        if (!isSpeakerPaymentAllowed(selectedReg)) {
            toast.error(SPEAKER_PAYMENT_BLOCKED_HELP_TEXT);
            return;
        }
        setIsProcessing(true);
        try {
            const { data } = await api.post<ConfirmPaymentResponse>(`/helpdesk/${selectedReg.id}/confirm-payment`, { justification }, {
                headers: { 'x-selected-event-id': selectedReg.eventId || selectedEventId }
            });

            if (data.emailSent === false) {
                toast.warning(selectedReg.isVirtualAttendance
                    ? 'Pago confirmado, pero falló el correo de confirmación. El acceso Zoom permanece programado.'
                    : 'Pago confirmado, pero falló envío. Use Reenviar Ticket');
            } else {
                toast.success("Pago confirmado y notificación enviada.");
            }
            setIsActionModalOpen(false);
            setJustification('');
            setShowPaymentConfirm(false);
            // Update local state
            setResults(prev => prev.map(r => r.id === selectedReg.id
                ? { ...r, status: data.status, paidAt: data.paidAt }
                : r));
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error al confirmar pago.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleViewCredential = async (regId: string) => {
        if (!isSpeakerPaymentAllowed(selectedReg)) {
            toast.error(SPEAKER_PAYMENT_BLOCKED_HELP_TEXT);
            return;
        }
        setIsProcessing(true);
        try {
            const isGlobalMode = searchScope === 'global';
            console.log(`[HelpDesk] ${isGlobalMode ? 'GLOBAL' : 'LOCAL'} View Credential Requested for RegID: ${regId}`);

            const { data } = await api.get(`/helpdesk/${regId}/credential`, {
                params: isGlobalMode ? { global: 'true' } : {},
                headers: { 'x-selected-event-id': selectedEventId }
            });

            setQrData({
                mode: data.mode === 'VIRTUAL' ? 'VIRTUAL' : 'QR',
                token: data.qrToken,
                folio: data.folio,
                nombre: data.nombre,
                eventName: data.eventName,
                date: data.date,
                time: data.time,
                timezone: data.timezone,
                zoomUrl: data.zoomUrl,
                passcode: data.passcode,
            });
            setIsQrModalOpen(true);
            setIsActionModalOpen(false);
        } catch (error: any) {
            console.error('[HelpDesk] Error ViewCredential:', error);
            toast.error(error.response?.data?.message || "Error al obtener la credencial.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Render QR when modal opens
    useEffect(() => {
        if (isQrModalOpen && qrData?.mode === 'QR' && qrData.token) {
            const qrToken = qrData.token;
            // Use requestAnimationFrame to ensure canvas is mounted in the DOM
            // This fixes the race condition where canvas ref might be null during Dialog animation
            requestAnimationFrame(() => {
                const canvas = qrCanvasRef.current;
                if (canvas) {
                    QRCode.toCanvas(canvas, qrToken, {
                        width: 300,
                        margin: 2,
                        color: {
                            dark: '#0f2744',
                            light: '#ffffff'
                        }
                    }, (error) => {
                        if (error) console.error("Error rendering QR to canvas", error);
                    });
                }
            });
        }
    }, [isQrModalOpen, qrData]);

    const handleCreateVip = async () => {
        if (!vipForm.nombre || !vipForm.justification) {
            toast.error("El nombre y la justificación son requeridos.");
            return;
        }
        setIsProcessing(true);
        try {
            const { data } = await api.post('/helpdesk/vip', { ...vipForm, eventId: selectedEventId });
            toast.success("Invitado VIP registrado exitosamente.");
            setIsVipModalOpen(false);
            setResults(prev => [data, ...prev]);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error al registrar VIP.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUpgradeParticipant = async () => {
        if (!selectedReg) return;

        setIsProcessing(true);
        try {
            const { data } = await api.patch(
                `/registrations/${selectedReg.id}/upgrade`,
                {},
                {
                    headers: { 'x-selected-event-id': selectedEventId }
                }
            );

            toast.success(data?.message || "Participante actualizado correctamente.");
            setResults((prev) => prev.map((registration) => (
                registration.id === selectedReg.id
                    ? {
                        ...registration,
                        status: data.registration?.status || 'PENDING',
                        participationMode: data.registration?.participationMode || 'ASISTENTE_UNACH',
                      }
                    : registration
            )));
            setIsActionModalOpen(false);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Error al actualizar participante.");
        } finally {
            setIsProcessing(false);
        }
    };

    const virtualAccessDue = Boolean(
        selectedReg?.virtualAccessScheduledSendAt &&
        new Date(selectedReg.virtualAccessScheduledSendAt).getTime() <= Date.now(),
    );
    const virtualAccessAlreadySent = Boolean(selectedReg?.virtualAccessSentAt);
    const virtualAccessActionDisabled = Boolean(
        selectedReg?.isVirtualAttendance && (
            !selectedReg.paidAt ||
            (!virtualAccessAlreadySent && !virtualAccessDue)
        ),
    );

    return (
        <div className="space-y-6 animate-in fade-in-50 duration-500">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-3xl font-bold text-[#0f2744]">Mesa de Ayuda</h2>
                    <p className="text-gray-500">Soporte técnico y contingencias en sitio.</p>
                </div>
                <Button
                    onClick={() => setIsVipModalOpen(true)}
                    className="bg-teal-600 hover:bg-teal-700 text-white shadow-md transition-all hover:scale-105"
                >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Nueva Cortesía / VIP
                </Button>
            </div>

            <Card className="border-none shadow-xl overflow-hidden bg-white/80 backdrop-blur-sm">
                <CardHeader className="bg-[#1e3a5f] text-white py-6">
                    <div className="flex items-center gap-3">
                        <div className="bg-white/20 p-2 rounded-lg">
                            <Search className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <CardTitle>Buscador de Asistentes</CardTitle>
                            <CardDescription className="text-blue-100">Ingrese el folio, nombre o correo del participante.</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-4 mb-8">
                        <div className="relative flex-1">
                            <Input
                                placeholder={searchScope === 'global' ? "Búsqueda global (folio, nombre, email o pulsera)..." : "Buscar en este evento (folio, nombre, email o pulsera)..."}
                                className="text-lg h-14 pl-12 border-2 border-gray-100 focus:border-[#1e3a5f] transition-all rounded-xl"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                        </div>

                        {canGlobalSearch && (
                            <Select value={searchScope} onValueChange={(val: any) => setSearchScope(val)}>
                                <SelectTrigger className="w-full md:w-[220px] h-14 border-2 border-gray-100 rounded-xl bg-white font-semibold text-[#0f2744]">
                                    <SelectValue placeholder="Alcance de búsqueda" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    <SelectItem value="event" className="py-3">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="h-4 w-4 text-blue-600" />
                                            <span>Solo este evento</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="global" className="py-3">
                                        <div className="flex items-center gap-2">
                                            <Shield className="h-4 w-4 text-[#c9a227]" />
                                            <span>Búsqueda Global</span>
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        )}

                        <Select value={roleFilter} onValueChange={(val: 'ALL' | 'ATTENDEE' | 'SPEAKER') => setRoleFilter(val)}>
                            <SelectTrigger className="w-full md:w-[180px] h-14 border-2 border-gray-100 rounded-xl bg-white font-semibold text-[#0f2744]">
                                <SelectValue placeholder="Rol" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectItem value="ALL">Todos los roles</SelectItem>
                                <SelectItem value="ATTENDEE">Asistente</SelectItem>
                                <SelectItem value="SPEAKER">Ponente</SelectItem>
                            </SelectContent>
                        </Select>

                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full md:w-[220px] h-14 border-2 border-gray-100 rounded-xl bg-white font-semibold text-[#0f2744]">
                                <SelectValue placeholder="Estado" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectItem value="ALL">Todos los estados</SelectItem>
                                <SelectItem value="PENDING">Pendiente</SelectItem>
                                <SelectItem value="PENDING_REVIEW">En revision</SelectItem>
                                <SelectItem value="APPROVED_TO_PAY">Aprobado para pago</SelectItem>
                                <SelectItem value="PAID">Pagado</SelectItem>
                                <SelectItem value="MATERIAL_UPLOADED">Material cargado</SelectItem>
                                <SelectItem value="CREDENTIAL_ISSUED">Credencial emitida</SelectItem>
                                <SelectItem value="REJECTED">Rechazado</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {searchValidationError && (
                        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            {searchValidationError}
                        </div>
                    )}

                    <div className="rounded-xl border border-gray-100 overflow-hidden shadow-sm bg-white">
                        <Table>
                            <TableHeader className="bg-gray-50">
                                <TableRow>
                                    <TableHead className="font-bold text-[#0f2744]">Folio</TableHead>
                                    <TableHead className="font-bold text-[#0f2744]">Nombre Completo</TableHead>
                                    {searchScope === 'global' && <TableHead className="font-bold text-[#0f2744]">Evento</TableHead>}
                                    <TableHead className="font-bold text-[#0f2744]">Estado</TableHead>
                                    <TableHead className="font-bold text-[#0f2744] text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={searchScope === 'global' ? 5 : 4} className="h-40 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
                                                <span className="text-gray-400 font-medium">Buscando en tiempo real...</span>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : results.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={searchScope === 'global' ? 5 : 4} className="h-60 text-center border-dashed border-2 border-gray-50">
                                            <div className="opacity-40 flex flex-col items-center gap-3">
                                                <Search className="h-12 w-12 text-gray-300" />
                                                <p className="text-lg text-gray-400">
                                                    {searchScope === 'event' && !selectedEventId
                                                        ? 'Seleccione un evento para cargar registros.'
                                                        : searchQuery
                                                            ? 'No se encontraron resultados.'
                                                            : 'No hay registros para los filtros seleccionados.'
                                                    }
                                                </p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    results.map((reg) => (
                                        <TableRow key={reg.id} className="hover:bg-blue-50/30 transition-colors group">
                                            <TableCell className="font-mono font-bold text-gray-600">
                                                {reg.folio}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-gray-900">{reg.nombre} {reg.apellidoPaterno} {reg.apellidoMaterno}</span>
                                                    <span className="text-xs text-gray-500">{reg.correo}</span>
                                                    {reg.participationMode === 'OYENTE_UNACH' && (
                                                        <Badge variant="outline" className="mt-2 w-fit border-amber-200 bg-amber-50 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                                                            Oyente UNACH
                                                        </Badge>
                                                    )}
                                                    {reg.participationMode === 'ASISTENTE_UNACH' && (
                                                        <Badge variant="outline" className="mt-2 w-fit border-blue-200 bg-blue-50 text-[10px] font-bold uppercase tracking-wide text-blue-700">
                                                            Asistente UNACH
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            {searchScope === 'global' && (
                                                <TableCell>
                                                    <Badge variant="outline" className="text-[10px] font-bold text-blue-800 bg-blue-50 border-blue-100">
                                                        {reg.event?.name || 'Evento Desconocido'}
                                                    </Badge>
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                {(() => {
                                                    const info = reg.isVirtualAttendance && reg.status === 'CREDENTIAL_ISSUED'
                                                        ? { label: 'Acceso Virtual Enviado', kind: 'paid' as const, showCheck: true }
                                                        : getRegStatusInfo(reg.status);
                                                    let className = "";
                                                    switch (info.kind) {
                                                        case 'paid': className = "bg-green-100 text-green-700 hover:bg-green-200"; break;
                                                        case 'pending': className = "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"; break;
                                                        case 'review': className = "bg-orange-100 text-orange-700 hover:bg-orange-200"; break;
                                                        case 'rejected': className = "bg-red-100 text-red-700 hover:bg-red-200"; break;
                                                        case 'approved_to_pay': className = "bg-blue-100 text-blue-700 hover:bg-blue-200"; break;
                                                        default: className = "bg-gray-100 text-gray-700";
                                                    }

                                                    return (
                                                        <Badge className={`${className} border-none px-3 py-1 font-medium transition-all`}>
                                                            {info.showCheck ? <CheckCircle2 className="h-3 w-3 mr-1 inline" /> : <Clock className="h-3 w-3 mr-1 inline" />}
                                                            {info.label}
                                                        </Badge>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    className="bg-[#1e3a5f] text-white hover:bg-[#0f2744] hover:scale-105 transition-all"
                                                    onClick={() => {
                                                        setSelectedReg(reg);
                                                        setSyswebStatus('NOT_QUERIED');
                                                        setSyswebCheckedAt(null);
                                                        setShowPaymentConfirm(false);
                                                        setJustification('');
                                                        setIsActionModalOpen(true);
                                                    }}
                                                >
                                                    <ExternalLink className="h-4 w-4 mr-2" />
                                                    Gestionar
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <p className="text-sm text-gray-500">
                            Pagina {meta.page} de {Math.max(meta.totalPages, 1)} - {meta.total} registros
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={!meta.hasPrev || isLoading}
                                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                            >
                                Anterior
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={!meta.hasNext || isLoading}
                                onClick={() => setPage((prev) => prev + 1)}
                            >
                                Siguiente
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Action Manager Modal */}
            <Dialog
                open={isActionModalOpen}
                onOpenChange={(open) => {
                    setIsActionModalOpen(open);
                    if (!open) {
                        setSyswebStatus('NOT_QUERIED');
                        setSyswebCheckedAt(null);
                        setShowPaymentConfirm(false);
                        setJustification('');
                    }
                }}
            >
                <DialogContent className="sm:max-w-[600px] border-none shadow-2xl overflow-hidden p-0">
                    <div className="bg-[#1e3a5f] p-8 text-white relative overflow-hidden">
                        <div className="relative z-10">
                            <h2 className="text-2xl font-bold flex items-center gap-2">
                                <UserPlus className="h-6 w-6" />
                                Gestión de Participante
                            </h2>
                            <p className="text-blue-100 mt-1 opacity-80">{selectedReg?.nombre} {selectedReg?.apellidoPaterno}</p>
                        </div>
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <ShieldIcon className="h-32 w-32" />
                        </div>
                    </div>

                    <div className="p-8 space-y-8">
                        {selectedReg && !isSpeakerPaymentAllowed(selectedReg) && (
                            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                                <p className="font-semibold">Acciones financieras bloqueadas</p>
                                <p className="mt-1">{SPEAKER_PAYMENT_BLOCKED_HELP_TEXT}</p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <ActionCard
                                icon={Mail}
                                title={selectedReg?.isVirtualAttendance
                                    ? (virtualAccessAlreadySent ? 'Reenviar acceso Zoom' : 'Enviar acceso Zoom')
                                    : 'Reenviar Ticket'}
                                description={selectedReg?.isVirtualAttendance
                                    ? (virtualAccessAlreadySent
                                        ? 'Envía nuevamente los datos vigentes de acceso virtual.'
                                        : 'Envía el acceso pendiente cuando la fecha programada ya fue alcanzada.')
                                    : 'Envía el QR por correo nuevamente.'}
                                color="blue"
                                onClick={() => handleResendQR(selectedReg!.id)}
                                disabled={isProcessing || !selectedReg?.correo || !isSpeakerPaymentAllowed(selectedReg) || virtualAccessActionDisabled || Boolean(
                                    selectedReg &&
                                    !selectedReg.isVirtualAttendance &&
                                    (!canResendPhysicalTicket || !isPaidStatus(selectedReg.status))
                                )}
                                disabledReason={!selectedReg?.isVirtualAttendance && !canResendPhysicalTicket
                                    ? 'No tienes permiso para reenviar credenciales en este evento.'
                                    : !selectedReg?.isVirtualAttendance && !isPaidStatus(selectedReg?.status || '')
                                        ? 'No disponible: el registro no ha sido pagado/confirmado.'
                                    : selectedReg?.isVirtualAttendance && !selectedReg?.paidAt
                                    ? 'No disponible: el registro virtual no tiene pago confirmado.'
                                    : selectedReg?.isVirtualAttendance && !virtualAccessAlreadySent && !virtualAccessDue
                                        ? 'No disponible hasta la fecha programada de envío.'
                                    : !isSpeakerPaymentAllowed(selectedReg) ? SPEAKER_PAYMENT_BLOCKED_HELP_TEXT : undefined}
                            />
                            {canManualPayment && (
                                <ActionCard
                                    icon={CreditCard}
                                    title="Pago Manual"
                                    description="Confirmar pago fuera de Sysweb."
                                    color="orange"
                                    onClick={() => setShowPaymentConfirm(true)}
                                    disabled={isProcessing || isPaidStatus(selectedReg?.status || '') || !isSpeakerPaymentAllowed(selectedReg)}
                                    disabledReason={!isSpeakerPaymentAllowed(selectedReg) ? SPEAKER_PAYMENT_BLOCKED_HELP_TEXT : undefined}
                                />
                            )}
                            <ActionCard
                                icon={QrIcon}
                                title={selectedReg?.isVirtualAttendance ? 'Visualizar acceso Zoom' : 'Visualizar QR'}
                                description={selectedReg?.isVirtualAttendance ? 'Consultar el acceso virtual vigente.' : 'Mostrar credencial en pantalla.'}
                                color="green"
                                onClick={() => handleViewCredential(selectedReg!.id)}
                                disabled={isProcessing || !isPaidStatus(selectedReg?.status || '') || !isSpeakerPaymentAllowed(selectedReg) || Boolean(selectedReg?.isVirtualAttendance && !virtualAccessAlreadySent)}
                                disabledReason={!isSpeakerPaymentAllowed(selectedReg) ? SPEAKER_PAYMENT_BLOCKED_HELP_TEXT : undefined}
                            />
                            <ActionCard
                                icon={isCheckingSysweb ? Loader2 : RefreshCw}
                                title="Consultar pago SYSWEB"
                                description="Consulta el estado remoto sin aplicar cambios al registro."
                                color="blue"
                                onClick={handleCheckSyswebStatus}
                                disabled={isCheckingSysweb || !selectedReg}
                            />
                            {syswebStatus === 'CONFIRMED' && canSyncSyswebPayment && (
                                <ActionCard
                                    icon={CheckCircle2}
                                    title="Aplicar pago confirmado"
                                    description="Revalidara el pago en SYSWEB antes de aplicarlo en CIU."
                                    color="green"
                                    onClick={handleApplySyswebPayment}
                                    disabled={isProcessing || !selectedReg}
                                />
                            )}
                            {canUpgradeParticipant && selectedReg?.status === 'PAID' && selectedReg?.participationMode === 'OYENTE_UNACH' && (
                                <ActionCard
                                    icon={ArrowUpCircle}
                                    title="Upgrade a pago"
                                    description="Convierte a asistente UNACH y regresa el registro a pendiente de pago."
                                    color="orange"
                                    onClick={handleUpgradeParticipant}
                                    disabled={isProcessing}
                                />
                            )}
                        </div>

                        <SyswebStatusIndicator state={syswebStatus} checkedAt={syswebCheckedAt} />

                        {canUpgradeParticipant && selectedReg?.status === 'PAID' && selectedReg?.participationMode === 'OYENTE_UNACH' && (
                            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                                <p className="font-semibold">Upgrade UNACH disponible</p>
                                <p className="mt-1">
                                    Este registro fue creado como <strong>OYENTE_UNACH</strong>. Si deseas cobrar kit y constancia, usa la acción
                                    <strong> Upgrade a pago</strong>.
                                </p>
                            </div>
                        )}

                        {canManualPayment && showPaymentConfirm && (
                            <div className="space-y-4 p-6 bg-orange-50 rounded-2xl border-2 border-orange-200 animate-in slide-in-from-top-4 duration-300">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="h-6 w-6 text-orange-600 mt-1" />
                                    <div>
                                        <h3 className="font-bold text-orange-900">Confirmación de Pago Manual</h3>
                                        <p className="text-sm text-orange-700">Esta acción es irreversible y será auditada bajo su usuario.</p>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-orange-900 font-bold">Justificación Auditada *</Label>
                                    <Textarea
                                        placeholder="Ingrese el motivo del pago manual (ej: Efectivo en sitio, patrocinio, etc.)"
                                        className="border-orange-200 focus:border-orange-500 min-h-[100px]"
                                        value={justification}
                                        onChange={(e) => setJustification(e.target.value)}
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                                        onClick={handleConfirmPayment}
                                        disabled={isProcessing || justification.length < 5}
                                    >
                                        {isProcessing ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                                        Confirmar Pago Final
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="border-orange-200 text-orange-700 hover:bg-orange-100"
                                        onClick={() => setShowPaymentConfirm(false)}
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            </div>
                        )}

                        <div className="pt-4 border-t border-gray-100 flex justify-between items-center text-xs text-gray-400">
                            <span>ID de Registro: {selectedReg?.id}</span>
                            <span>Mesa de Ayuda v1.0</span>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* QR Visualization Modal */}
            <Dialog open={isQrModalOpen} onOpenChange={(open) => {
                setIsQrModalOpen(open);
                if (!open) setQrData(null); // Efímero: borrar token al cerrar
            }}>
                <DialogContent className="sm:max-w-[450px] border-none shadow-2xl p-0 overflow-hidden bg-white">
                    <div className="bg-[#0f2744] p-6 text-center text-white">
                        {qrData?.mode === 'VIRTUAL'
                            ? <ExternalLink className="h-10 w-10 mx-auto mb-2 opacity-50" />
                            : <QrIcon className="h-10 w-10 mx-auto mb-2 opacity-50" />}
                        <h2 className="text-xl font-bold uppercase tracking-tight">
                            {qrData?.mode === 'VIRTUAL' ? 'Acceso Zoom' : 'Credencial Digital'}
                        </h2>
                        <p className="text-blue-200 text-xs">
                            {qrData?.mode === 'VIRTUAL' ? 'Atención virtual - UNACH' : 'Atención en sitio - UNACH'}
                        </p>
                    </div>

                    <div className="p-8 flex flex-col items-center">
                        <div className="text-center mb-6">
                            <h3 className="text-xl font-bold text-gray-900">{qrData?.nombre}</h3>
                            <p className="text-sm font-semibold text-[#c9a227]">{qrData?.folio}</p>
                        </div>

                        {qrData?.mode === 'VIRTUAL' ? (
                            <div className="w-full rounded-2xl border border-blue-100 bg-blue-50 p-5 text-sm text-blue-950 space-y-3">
                                <p><strong>Fecha:</strong> {qrData.date}</p>
                                <p><strong>Hora:</strong> {qrData.time} hrs ({qrData.timezone})</p>
                                {qrData.passcode && <p><strong>Código de acceso:</strong> {qrData.passcode}</p>}
                                <a
                                    href={qrData.zoomUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 font-semibold text-blue-700 underline break-all"
                                >
                                    Abrir enlace de Zoom <ExternalLink className="h-4 w-4 shrink-0" />
                                </a>
                                <p className="rounded-lg bg-white p-3 text-xs text-slate-600 border border-blue-100">
                                    No se emite código QR para esta modalidad.
                                </p>
                            </div>
                        ) : (
                            <div className="bg-white p-3 rounded-2xl border-2 border-dashed border-gray-100 shadow-inner">
                                <canvas ref={qrCanvasRef} className="max-w-full h-auto" />
                            </div>
                        )}

                        <div className="w-full mt-8 p-4 bg-gray-50 rounded-xl border border-gray-100 flex flex-col gap-2">
                            <div className="flex justify-between items-center text-[11px]">
                                <span className="text-gray-400 uppercase font-bold tracking-wider">Evento</span>
                                <span className="text-gray-700 font-semibold truncate max-w-[200px]">{qrData?.eventName}</span>
                            </div>
                            <div className="flex justify-between items-center text-[11px]">
                                <span className="text-gray-400 uppercase font-bold tracking-wider">Seguridad</span>
                                <span className="text-teal-600 font-bold">
                                    {qrData?.mode === 'VIRTUAL' ? 'Acceso autorizado' : '✓ Token Persistente'}
                                </span>
                            </div>
                        </div>

                        <p className="mt-6 text-[10px] text-center text-gray-400 leading-relaxed">
                            {qrData?.mode === 'VIRTUAL'
                                ? 'Comparta estos datos solo con el asistente autorizado.'
                                : 'Permita que el asistente fotografíe este código con su móvil.'}<br />
                            Esta visualización ha sido registrada en el log de auditoría.
                        </p>
                    </div>

                    <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-center">
                        <Button
                            variant="outline"
                            className="w-full border-gray-200 text-gray-600 hover:bg-gray-100"
                            onClick={() => setIsQrModalOpen(false)}
                        >
                            Cerrar Visualización
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* VIP Registration Modal */}
            <Dialog open={isVipModalOpen} onOpenChange={setIsVipModalOpen}>
                <DialogContent className="sm:max-w-[700px] border-none shadow-2xl p-0 overflow-hidden">
                    <div className="bg-teal-600 p-8 text-white">
                        <h2 className="text-2xl font-bold">Registro de Cortesía / VIP</h2>
                        <p className="text-teal-100 opacity-80">Alta inmediata de asistentes especiales sin costo.</p>
                    </div>

                    <div className="p-8 max-h-[70vh] overflow-y-auto">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label className="font-bold">Nombre *</Label>
                                <Input
                                    className="bg-gray-50 h-11"
                                    placeholder="Ej: Juan"
                                    value={vipForm.nombre}
                                    onChange={(e) => setVipForm({ ...vipForm, nombre: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-bold">Apellido Paterno *</Label>
                                <Input
                                    className="bg-gray-50 h-11"
                                    placeholder="Ej: Pérez"
                                    value={vipForm.apellidoPaterno}
                                    onChange={(e) => setVipForm({ ...vipForm, apellidoPaterno: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-bold">Apellido Materno</Label>
                                <Input
                                    className="bg-gray-50 h-11"
                                    placeholder="Ej: Gómez"
                                    value={vipForm.apellidoMaterno}
                                    onChange={(e) => setVipForm({ ...vipForm, apellidoMaterno: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-bold">Correo Electrónico</Label>
                                <Input
                                    className="bg-gray-50 h-11"
                                    placeholder="asistente@ejemplo.com"
                                    value={vipForm.correo}
                                    onChange={(e) => setVipForm({ ...vipForm, correo: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-bold">Institución / Dependencia</Label>
                                <Input
                                    className="bg-gray-50 h-11"
                                    placeholder="UNACH, CIU, etc."
                                    value={vipForm.institucion}
                                    onChange={(e) => setVipForm({ ...vipForm, institucion: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-bold">Tipo de Participante</Label>
                                <Select
                                    value={vipForm.tipoParticipante}
                                    onValueChange={(val) => setVipForm({ ...vipForm, tipoParticipante: val })}
                                >
                                    <SelectTrigger className="bg-gray-50 h-11">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="estudiante-unach">Estudiante UNACH</SelectItem>
                                        <SelectItem value="estudiante-externo">Estudiante Externo</SelectItem>
                                        <SelectItem value="docente-unach">Docente UNACH</SelectItem>
                                        <SelectItem value="profesionista">Profesionista</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="sm:col-span-2 space-y-2 pt-4 border-t border-gray-100">
                                <Label className="font-bold text-teal-700 flex items-center gap-2">
                                    <ShieldIcon className="h-4 w-4" />
                                    Justificación de la Cortesía *
                                </Label>
                                <Textarea
                                    placeholder="Indique por qué se otorga la cortesía (ej: Invitado especial, Ponente magistral, etc.)"
                                    className="min-h-[120px] bg-teal-50/30 border-teal-100 focus:border-teal-400"
                                    value={vipForm.justification}
                                    onChange={(e) => setVipForm({ ...vipForm, justification: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="p-8 bg-gray-50 flex justify-end gap-3">
                        <Button variant="ghost" onClick={() => setIsVipModalOpen(false)}>Cancelar</Button>
                        <Button
                            className="bg-teal-600 hover:bg-teal-700 text-white min-w-[150px]"
                            onClick={handleCreateVip}
                            disabled={isProcessing}
                        >
                            {isProcessing ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                            Completar Registro
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Support components for cleaner UI
function ActionCard({ icon: Icon, title, description, color, onClick, disabled, disabledReason }: any) {
    const colorClasses: any = {
        blue: "bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100 hover:border-blue-200",
        orange: "bg-orange-50 text-orange-700 border-orange-100 hover:bg-orange-100 hover:border-orange-200",
        green: "bg-green-50 text-green-700 border-green-100 hover:bg-green-100 hover:border-green-200"
    };

    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`
                flex flex-col items-center text-center p-6 rounded-2xl border-2 transition-all 
                ${colorClasses[color]} 
                ${disabled ? 'opacity-50 cursor-not-allowed grayscale' : 'cursor-pointer hover:scale-102 active:scale-95 shadow-sm'}
            `}
            title={disabledReason || description}
        >
            <div className="p-3 bg-white rounded-xl shadow-sm mb-4">
                <Icon className="h-6 w-6" />
            </div>
            <h3 className="font-bold mb-1">{title}</h3>
            <p className="text-xs opacity-70 px-2 leading-tight">{description}</p>
            {disabledReason && (
                <p className="mt-3 rounded-lg bg-white/80 px-2 py-1 text-[11px] font-semibold leading-snug text-amber-700">
                    {disabledReason}
                </p>
            )}
        </button>
    );
}

function SyswebStatusIndicator({ state, checkedAt }: { state: SyswebStatusState; checkedAt: string | null }) {
    const statusConfig = {
        NOT_QUERIED: {
            label: 'No consultado',
            description: null,
            icon: Clock,
            className: 'border-gray-200 bg-gray-50 text-gray-700',
        },
        CONFIRMED: {
            label: 'Pago confirmado por SYSWEB',
            description: null,
            icon: CheckCircle2,
            className: 'border-green-200 bg-green-50 text-green-800',
        },
        NOT_CONFIRMED: {
            label: 'Pago no confirmado en SYSWEB',
            description: 'Revise este folio en SYSWEB antes de continuar.',
            icon: AlertCircle,
            className: 'border-red-200 bg-red-50 text-red-800',
        },
        UNAVAILABLE: {
            label: 'No fue posible consultar SYSWEB',
            description: 'Intente nuevamente. Si el problema continúa, revise el caso en SYSWEB.',
            icon: AlertCircle,
            className: 'border-amber-200 bg-amber-50 text-amber-800',
        },
    }[state];
    const StatusIcon = statusConfig.icon;

    return (
        <div className={`flex items-start gap-3 rounded-2xl border p-4 ${statusConfig.className}`} role="status">
            <StatusIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <div>
                <p className="font-semibold">{statusConfig.label}</p>
                {statusConfig.description && (
                    <p className="mt-1 text-sm opacity-90">{statusConfig.description}</p>
                )}
                {checkedAt && (
                    <p className="mt-1 text-xs opacity-75">
                        Consultado: {new Date(checkedAt).toLocaleString('es-MX')}
                    </p>
                )}
            </div>
        </div>
    );
}

function ShieldIcon({ className }: { className?: string }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z" />
        </svg>
    );
}
